import { useEffect, useState } from "react";
import type { KeyboardEvent, MouseEvent, ReactNode } from "react";
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type { DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Button from "@/components/Button/Button";
import DragGripIcon from "@/components/SVGComponents/DragGripIcon";
import RowActionsMenu from "@/components/ActionCells/RowActionsMenu";
import type { RowAction } from "@/components/ActionCells/RowActionsMenu";
import { combineClasses } from "@/lib/utils";
import styles from "./MobileList.module.scss";

interface MobileListProps {
  isLoading: boolean;
  isEmpty: boolean;
  emptyTitle: string;
  emptyCaption?: string;
  skeletonCount?: number;
  className?: string;
  children: ReactNode;
}

// Mobile stand-in for a Table: one bordered card of `MobileListRow`s,
// with its own loading skeleton and empty state.
const MobileList = ({
  isLoading,
  isEmpty,
  emptyTitle,
  emptyCaption,
  skeletonCount = 5,
  className,
  children,
}: MobileListProps) => {
  let content: ReactNode = children;

  if (isLoading) {
    content = Array.from({ length: skeletonCount }).map((_, idx) => (
      <div key={idx} className={styles.skeletonRow}>
        <div className={styles.skeletonLineShort} />
        <div className={styles.skeletonLineLong} />
      </div>
    ));
  } else if (isEmpty) {
    content = (
      <div className={styles.emptyState}>
        <h4 className={styles.emptyTitle}>{emptyTitle}</h4>
        {emptyCaption && <p className={styles.emptyCaption}>{emptyCaption}</p>}
      </div>
    );
  }

  return (
    <div className={combineClasses(styles.list, className)}>{content}</div>
  );
};

export interface MobileListPanelAction {
  key: string;
  label: string;
  icon?: ReactNode;
  outlined?: boolean;
  onClick: () => void;
}

interface MobileListPanelProps {
  // Search/filter controls, stacked.
  children?: ReactNode;
  // Rendered as equal-width buttons along the bottom (e.g. Export / Add).
  actions?: MobileListPanelAction[];
}

// The card that sits above a MobileList holding its search, filters and
// page-level actions — mobile counterpart of the Table header.
export const MobileListPanel = ({
  children,
  actions = [],
}: MobileListPanelProps) => (
  <div className={styles.panel}>
    {children}
    {actions.length > 0 && (
      <div
        className={styles.panelActions}
        style={{
          gridTemplateColumns: `repeat(${actions.length}, minmax(0, 1fr))`,
        }}
      >
        {actions.map((action) => (
          <Button
            key={action.key}
            outlined={action.outlined ?? false}
            onClick={action.onClick}
            className={styles.panelActionBtn}
          >
            {action.icon && (
              <span className={styles.panelActionIcon}>{action.icon}</span>
            )}
            <span>{action.label}</span>
          </Button>
        ))}
      </div>
    )}
  </div>
);

export interface MobileListMetaItem {
  key: string;
  value: ReactNode;
  // Small uppercase label look — e.g. a persona like "FOUNDER".
  emphasis?: boolean;
}

interface MobileListRowProps {
  title: string;
  badge?: ReactNode;
  // Items with an empty value are skipped; the rest are joined with "•".
  meta?: MobileListMetaItem[];
  actions?: RowAction[];
  onClick?: () => void;
  ariaLabel?: string;
  // Rendered before the title block — e.g. SortableMobileList's drag handle.
  leading?: ReactNode;
}

export const MobileListRow = ({
  title,
  badge,
  meta = [],
  actions = [],
  onClick,
  ariaLabel,
  leading,
}: MobileListRowProps) => {
  const visibleMeta = meta.filter(
    (item) =>
      item.value !== null && item.value !== undefined && item.value !== "",
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (!onClick) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick();
    }
  };

  const stopPropagation = (e: MouseEvent) => e.stopPropagation();

  return (
    <div
      className={combineClasses(styles.row, onClick && styles.clickable)}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? "button" : undefined}
      tabIndex={onClick ? 0 : undefined}
      aria-label={ariaLabel}
    >
      {leading && (
        <div className={styles.rowLeading} onClick={stopPropagation}>
          {leading}
        </div>
      )}
      <div className={styles.rowBody}>
        <div className={styles.rowTop}>
          <span className={styles.rowTitle}>{title}</span>
          {(badge || actions.length > 0) && (
            <div className={styles.rowTrailing} onClick={stopPropagation}>
              {badge}
              {actions.length > 0 && (
                <RowActionsMenu
                  actions={actions}
                  ariaLabel={`Actions for ${title}`}
                  triggerClassName={styles.actionTrigger}
                />
              )}
            </div>
          )}
        </div>
        {visibleMeta.length > 0 && (
          <p className={styles.rowMeta}>
            {visibleMeta.map((item) => (
              <span
                key={item.key}
                className={item.emphasis ? styles.metaEmphasis : undefined}
              >
                {item.value}
              </span>
            ))}
          </p>
        )}
      </div>
    </div>
  );
};

interface SortableMobileListItemProps {
  id: string | number;
  disabled: boolean;
  children: (dragHandle: ReactNode) => ReactNode;
}

const SortableMobileListItem = ({
  id,
  disabled,
  children,
}: SortableMobileListItemProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  // x is pinned to 0 so a dragged row only moves vertically. Like
  // DraggableQuestionTable's SortableRow, `zIndex` is only set while
  // dragging so idle rows don't each become a stacking context.
  const style = {
    transform: CSS.Transform.toString(transform && { ...transform, x: 0 }),
    transition,
    ...(isDragging && { position: "relative" as const, zIndex: 2 }),
  };

  const dragHandle = disabled ? null : (
    <button
      type="button"
      ref={setActivatorNodeRef}
      {...attributes}
      {...listeners}
      className={styles.dragHandle}
      aria-label="Drag to reorder"
    >
      <DragGripIcon />
    </button>
  );

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={combineClasses(
        styles.sortableItem,
        isDragging && styles.dragging,
      )}
    >
      {children(dragHandle)}
    </div>
  );
};

interface SortableMobileListProps<T extends { id: string | number }> {
  items: T[];
  // Return `false` to reject the new order — the row snaps back.
  onReorder: (reordered: T[]) => boolean | void;
  // Hides the drag handles, e.g. without edit permission or mid-save.
  disabled?: boolean;
  // Render a MobileListRow and pass `dragHandle` as its `leading`.
  renderItem: (item: T, dragHandle: ReactNode, index: number) => ReactNode;
}

// Drag-to-reorder list of MobileListRows — the mobile counterpart of
// DraggableQuestionTable, with the same sensors. Only the grip handle
// starts a drag (and on touch only after a short hold), so swiping
// anywhere else on a row still scrolls the page.
export const SortableMobileList = <T extends { id: string | number }>({
  items,
  onReorder,
  disabled = false,
  renderItem,
}: SortableMobileListProps<T>) => {
  // Local copy so a dropped row lands immediately, before `onReorder`'s
  // save + refetch comes back — same approach as DraggableQuestionTable.
  const [orderedItems, setOrderedItems] = useState<T[]>(items);

  useEffect(() => {
    setOrderedItems(items);
  }, [items]);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 120, tolerance: 5 },
    }),
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const oldIndex = orderedItems.findIndex((item) => item.id === active.id);
    const newIndex = orderedItems.findIndex((item) => item.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const reordered = arrayMove(orderedItems, oldIndex, newIndex);
    setOrderedItems(reordered);
    if (onReorder(reordered) === false) setOrderedItems(orderedItems);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={orderedItems.map((item) => item.id)}
        strategy={verticalListSortingStrategy}
      >
        {orderedItems.map((item, index) => (
          <SortableMobileListItem
            key={item.id}
            id={item.id}
            disabled={disabled}
          >
            {(dragHandle) => renderItem(item, dragHandle, index)}
          </SortableMobileListItem>
        ))}
      </SortableContext>
    </DndContext>
  );
};

export default MobileList;
